(ns app.front.ui.v2.checkbox-picker
  (:require [app.front.ui :as ui]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.util :refer [for-frag]]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.util.util :as util]
            [reagent.core :as r]))

(defn- item-ids [items val->id]
  (into #{} (map val->id) items))

(defn- ordered-selected-items [values selected-ids val->id]
  (filterv #(contains? selected-ids (val->id %)) values))

(def ^:private checkbox-grid-classes
  (str/join " " [cc/carbon-checkbox-no-margin-class
                 cc/carbon-checkbox-wrap-label-class
                 cc/carbon-checkbox-auto-height-class]))

(defn checkbox-picker
  "Checkbox picker with two modes:

  Deferred draft mode (default): the user opens a grid of checkboxes, adjusts the
  selection, then clicks Done to commit and close. While open a local draft tracks
  changes; on Done, `on-save` is called with the new selection vector and the
  draft is cleared.

  Immediate toggle mode: pass `:on-toggle` instead of `:on-save`. Each checkbox
  change fires `(on-toggle item added?)` immediately and the rendered selection is
  driven entirely by the `selected-items` prop; no draft is kept and the Done
  button simply closes the panel.

  Props:
    :id              string, data-cy identifier
    :label-text      string, fieldset label (nil to hide)
    :values          vector of option maps
    :selected-items  vector of currently selected option maps; must be a subset of
                     `:values` — unknown selections are dropped on save/render
    :locked-items    vector of option maps that cannot be toggled
    :val->id         (item -> id), defaults to identity
    :val->str        (item -> string), defaults to str
    :on-save         (fn [new-selected-items]) called on Done — deferred mode
    :on-toggle       (fn [item added?]) called on each toggle — immediate mode
    :add-button-text string, text for the open button (required)
    :done-text       string, text for the Done button (required)
    :invalid?        boolean, show validation state
    :invalid-text    string, validation message
    :open-by-default? boolean, start with the grid open
    :disabled?       boolean, disable the open button (closed state only)"
  [{:keys [id
           label-text
           values
           selected-items
           locked-items
           val->id
           val->str
           on-save
           on-toggle
           add-button-text
           done-text
           invalid?
           invalid-text
           open-by-default?
           disabled?]
    :or   {val->id  identity
           val->str str}}]
  (r/with-let [*open?     (r/atom (boolean (and open-by-default? (not disabled?))))
               *draft-ids (r/atom nil)]
    (let [selected-ids        (item-ids selected-items val->id)
          locked-ids          (item-ids locked-items val->id)
          current-selection   (or @*draft-ids selected-ids)
          selected-pill-items (ordered-selected-items values current-selection val->id)]
      [:fieldset.flex.flex-col {:data-cy id}
       (when label-text
         [:label.bx--label.font-bold (when invalid? {:class "required-field"}) label-text])

       (when (seq selected-pill-items)
         [:div.flex.flex-wrap.gap-2.mb-3
          (for-frag [item selected-pill-items]
            ^{:key (str id "-pill-" (val->id item))}
            [ui-config-pills/pill {:class "bg-ui-night-sky-100"}
             (val->str item)])])

       (if @*open?
         [:div.rounded.border.border-solid.border-gray-400.overflow-hidden
          [:div.p-6.grid.gap-x-8.gap-y-4.grid-cols-1.md:grid-cols-2.xl:grid-cols-4
           {:class checkbox-grid-classes}
           (for-frag [item values]
             (let [item-id  (val->id item)
                   checked? (contains? current-selection item-id)
                   locked?  (contains? locked-ids item-id)]
               ^{:key (str id "-checkbox-" item-id)}
               [:div
                (cc/checkbox {:id         (str id "-checkbox-" item-id)
                              :checked    checked?
                              :disabled   locked?
                              :label-text (val->str item)
                              :on-change  #(if on-toggle
                                             (on-toggle item (not checked?))
                                             (swap! *draft-ids
                                                    (fn [draft-ids]
                                                      (util/toggle-in-set (or draft-ids selected-ids) item-id))))})]))]
          [:div.flex.justify-end.border-t.border-solid.border-gray-400.p-4
           [ui/button {:kind     :primary
                       :size     :small
                       :data-cy  (str id "-done")
                       :on-click #(let [latest-selection-ids (or @*draft-ids selected-ids)
                                        new-selection        (ordered-selected-items values latest-selection-ids val->id)]
                                    (when on-save
                                      (on-save new-selection))
                                    (reset! *draft-ids nil)
                                    (reset! *open? false))}
            done-text]]]
         [:div.min-w-4
          [ui/button {:kind     :secondary
                      :size     :small
                      :data-cy  (str id "-open")
                      :disabled disabled?
                      :on-click #(do
                                   (when-not on-toggle
                                     (reset! *draft-ids selected-ids))
                                   (reset! *open? true))}
           (str add-button-text " +")]])

       (when invalid?
         [:div.bx--form-requirement.mt-2 invalid-text])])))
